import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MvMVictoryModal } from '../MvMBattle/MvMVictoryModal'
import { mvmResultMutedStyle, mvmResultSummaryTextStyle } from '../MvMBattle/mvmBattleResultUi'

interface ConquerResult {
  kind: 'declined' | 'approved' | 'nullified'
  seizedResources?: Record<string, number> | null
  timedOut?: boolean
  allianceName?: string | null
  message?: string | null
}

/**
 * Global watcher (mounted once in App). When a delayed castle-conquer outcome arrives (the leader
 * approved/declined, or the 10-minute window lapsed) it re-shows the victory result modal wherever
 * the player is: a DECLINE re-displays the kept raid resources with a notice; an APPROVAL confirms
 * the castle joined the alliance (no resources); a NULLIFIED result means recruiting was impossible
 * (alliance full) so nothing happened at all. Driven by the `castle-conquer-result` window event.
 */
export const CastleConquerResultWatcher: React.FC = () => {
  const { t } = useTranslation()
  const [result, setResult] = useState<ConquerResult | null>(null)

  useEffect(() => {
    const onResult = (e: Event) => {
      const detail = (e as CustomEvent).detail as ConquerResult | undefined
      if (detail?.kind) setResult(detail)
    }
    window.addEventListener('castle-conquer-result', onResult)
    return () => window.removeEventListener('castle-conquer-result', onResult)
  }, [])

  if (!result) return null
  const close = () => setResult(null)

  if (result.kind === 'declined') {
    return (
      <MvMVictoryModal
        showResourceLoot
        seizedResources={result.seizedResources ?? null}
        showBattleRewards={false}
        descriptionContent={
          <div>
            <p style={mvmResultSummaryTextStyle}>
              {t('mvm.victory.conquerDeclinedTitle', { defaultValue: 'Conquest cancelled' })}
            </p>
            <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
              {result.timedOut
                ? t('mvm.victory.conquerTimedOut', {
                    defaultValue: 'Your leader didn’t respond in time. You keep the raid resources.',
                  })
                : t('mvm.victory.conquerDeclined', {
                    defaultValue: 'Your leader declined the conquest. You keep the raid resources.',
                  })}
            </p>
          </div>
        }
        dismissButtonLabel={t('common.ok', { defaultValue: 'OK' })}
        onClose={close}
      />
    )
  }

  if (result.kind === 'nullified') {
    return (
      <MvMVictoryModal
        showResourceLoot={false}
        seizedResources={null}
        showBattleRewards={false}
        descriptionContent={
          <div>
            <p style={mvmResultSummaryTextStyle}>
              {t('mvm.victory.conquerFailedTitle', { defaultValue: 'Castle not recruited' })}
            </p>
            <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
              {result.message ||
                t('mvm.victory.conquerFailed', {
                  defaultValue:
                    'There was no room to recruit the castle, so nothing was taken — you keep the battle experience.',
                })}
            </p>
          </div>
        }
        dismissButtonLabel={t('common.ok', { defaultValue: 'OK' })}
        onClose={close}
      />
    )
  }

  return (
    <MvMVictoryModal
      showResourceLoot={false}
      seizedResources={null}
      showBattleRewards={false}
      descriptionContent={
        <div>
          <p style={mvmResultSummaryTextStyle}>{t('mvm.victory.conqueredTitle', { defaultValue: 'Castle conquered!' })}</p>
          <p style={{ ...mvmResultMutedStyle, marginTop: 'clamp(0.4dvh, 0.6dvh, 6px)' }}>
            {t('mvm.victory.conqueredJoined', {
              defaultValue: 'The castle joined {{name}}.',
              name: result.allianceName || t('mvm.victory.yourAlliance', { defaultValue: 'your alliance' }),
            })}
          </p>
        </div>
      }
      dismissButtonLabel={t('common.ok', { defaultValue: 'OK' })}
      onClose={close}
    />
  )
}

export default CastleConquerResultWatcher
